/**
 * Day Card Component
 * Displays single day of itinerary with image, details, description
 * Used for main tour days (not pre/post nights or extensions)
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import ColletteReadMoreToggle from "@/components/common-controls/collette-read-more-toggle";
import { stripTags } from "@/utils/content-utils";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import DayUpgrades from "./day-upgrades";
import type { DayCardProps } from "../types";

const DayCard: React.FC<DayCardProps> = ({ day, dayNumber, testIdPrefix }) => {
  const t = useTranslations("PDP");
  const dayLabel = t(String(day.dayNumber).includes("&") ? "Days" : "Day");

  return (
    <Box
      data-testid={`${testIdPrefix}_day-card_${dayNumber}`}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", legacyDesktop: "row" },
        gap: "2.4rem",
        padding: { xs: "2.4rem 0", legacyTablet: "2.4rem 0", legacyDesktop: "2.4rem" }
        // WHY: Stack vertically on tablet/mobile (<992px), horizontal on desktop
        // WHY: Responsive padding matches other PDP components
      }}
    >
      {/* Day Image */}
      <Box
        sx={{
          width: { legacyDesktop: "45.6rem" },
          alignSelf: { xs: "stretch", legacyDesktop: "auto" },
          height: "30.7rem",
          borderRadius: "2rem",
          overflow: "hidden",
          flexShrink: 0,
          position: "relative"
          // WHY: Consistent 30.7rem height across all breakpoints
          // WHY: Stretch to full width on mobile/tablet (<992px), fixed 45.6rem on desktop
        }}

      >
        {day.dayVideo ? (
          <WistiaPlayer
            id={`tour-media-gallery-video-_day-card_${dayNumber}`}
            key={`${dayNumber}-${day.dayVideo}`} // Force re-mount when video changes
            data-testid={`${testIdPrefix}_day-card_${dayNumber}_video`}
            mediaId={day.dayVideo || ""}
            playBarControl={false}
            volumeControl={false}
            settingsControl={false}
            playerColor="var(--theme-palette-common-white)"
            roundedPlayer={20}
            fullscreenControl
            playPauseControl
            playPauseNotifier={false}
            controlsVisibleOnLoad={false}
            bigPlayButton
            seo
          />

        ) : (
          <Image
            src={day.imageUrl}
            alt={day.imageAltTag || `Image for Day ${dayNumber}`}
            fill
            sizes="(max-width: 992px) 100vw, 912px"
            style={{ objectFit: "cover" }}
            data-testid={`${testIdPrefix}_day-card_${dayNumber}_image`}
          />
        )}
      </Box>

      {/* Day Content */}
      <Stack gap="1.2rem" flex={1}>
        {/* Title: Day X: City */}
        <Typography
          component="h4"
          sx={{
            fontSize: { xs: "1.8rem", md: "2rem" },
            fontWeight: 600,
            lineHeight: 1.3,
            color: "text.primary",
            margin: 0
            // WHY: Matches Figma H5 specs - responsive sizing, Day# is 600, city is 500
          }}
          data-testid={`${testIdPrefix}_day_${dayNumber}`}
        >
          <Box component="span" sx={{ fontWeight: 600 }} data-testid={`${testIdPrefix}_day-label_${dayNumber}`}>
            {dayLabel} {day.dayNumber}:
          </Box>{" "}
          <Box component="span" sx={{ fontWeight: 500 }} data-testid={`${testIdPrefix}_day-heading_${dayNumber}`}>
            {day.dayHeading}
          </Box>
        </Typography>

        {/* Details: Accommodation & Meals */}
        <Stack gap="0.8rem">
          {day.accommodation && (
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                lineHeight: 1.5
                // WHY: Matches Figma subtitle 2
              }}
              data-testid={`${testIdPrefix}_day-accommodation_${dayNumber}`}
            >
              <Box component="span" sx={{ fontWeight: 700 }} data-testid={`${testIdPrefix}_day-accommodation-label_${dayNumber}`}>
                {t("Accommodation")}:{" "}
              </Box>
              {day.accommodation}
            </Typography>
          )}

          {day.meals && (
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                lineHeight: 1.5
              }}
              data-testid={`${testIdPrefix}_day-meals_${dayNumber}`}
            >
              <Box component="span" sx={{ fontWeight: 700 }} data-testid={`${testIdPrefix}_day-meals-label_${dayNumber}`}>
                Meals:{" "}
              </Box>
              {day.meals}
            </Typography>
          )}
        </Stack>

        {/* Description with Read More */}
        <ColletteReadMoreToggle
          description={stripTags(day.description)}
          linesShown={8}
          // WHY: Figma specs show ~4 lines before truncation
          testId={`${testIdPrefix}_day-card_${dayNumber}_description`}
          textSx={{
            fontSize: "1.6rem",
            lineHeight: 1.6,
            fontWeight: 400
          }}
        />

        {/* Upgrades (if any) */}
        {day.upgrades &&
          (day.upgrades.choiceOnTour.length !== 0 ||
            day.upgrades.excursion.length !== 0 ||
            day.upgrades.roomUpgrades.length !== 0) ? (
          <DayUpgrades upgrades={day.upgrades} dayNumber={dayNumber} testIdPrefix={testIdPrefix} />
        ) : null}
      </Stack>
    </Box>
  );
};

export default DayCard;
